import { Box, Card, CardBody, CardFooter, CardHeader, Center, IconButton, Image, Input, Link, Text, useToast, useDisclosure, Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, Flex, Checkbox, Badge } from "@chakra-ui/react"
import { faCancel, faCheck, faEdit, faTrash, faEye, faUpload } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useState, useRef } from "react"
import { matchMediaIcon } from "@/functions/mediaFunctions"
import { MediaMeta, MediaType } from "@/pages/mediaManager"
import { Viewer } from "@react-pdf-viewer/core"
import { useNavigate } from "react-router-dom"
import '@react-pdf-viewer/core/lib/styles/index.css'

import { useApp } from "@/hooks/contextHooks"
import { NewSection } from "@xnav/types/src/common"

export type MediaCardProps = {
  viewMode: 'list' | 'grid'
  onEdit: (newMedia: MediaMeta, oldData: MediaMeta) => void
  onDelete: (data: MediaMeta) => void
  onReplace?: (newMedia: MediaMeta, file: File, icon?: File) => void
  sectionMeta?: NewSection[]
  selectedIds?: string[]
  onToggleSelect?: (id: string) => void
}

function bytesToMB(bytes: any) {
  return Math.ceil(bytes / 1_000_000) + " MB";
}


function findPath(sections: NewSection[], targetId: string, currentPath: string[] = []): string[] | null {
  for (const item of sections) {
    const newPath = [...currentPath, item.title];
    if (item.id === targetId) {
      return newPath;
    }
    if (item.contents && item.contents.length > 0) {
      for (const child of item.contents) {
        if (child.id === targetId) {
          return [...newPath, child.title];
        }
        if (child.type === 'section') {
          const result = findPath([child as NewSection], targetId, newPath);
          if (result) return result;
        }
      }
    }
  }
  return null;
}

const getAcceptString = (type: MediaType) => {
  if (type === MediaType.Video) return 'video/*';
  if (type === MediaType.Image) return 'image/*';
  if (type === MediaType.Audio) return 'audio/*';
  if (type === MediaType.Pdf) return 'application/pdf';
  return '*/*';
};

const MediaCard: React.FC<MediaCardProps & { data: MediaMeta }> = ({ data, viewMode, onEdit, onDelete, sectionMeta, onReplace, selectedIds, onToggleSelect }) => {
  const navigate = useNavigate()
  const app = useApp()
  const [newName, setNewName] = useState<string>()
  const [isDeleting, setIsDeleting] = useState<boolean>(false)
  const [isWarning, setIsWarning] = useState<boolean>(false)
  const toast = useToast()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const resolvedPaths = (data.references || []).map(refId => {
    const path = sectionMeta ? findPath(sectionMeta, refId) : null;
    return path ? path.join(' > ') : null;
  }).filter(Boolean) as string[];

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && onReplace) {
      const newMedia = {
        ...data,
        size: file.size,
        uploaded: new Date()
      };
      onReplace(newMedia, file);
      toast({
        title: "Uploading replacement file",
        description: `${file.name} is uploading...`,
        status: "info",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="4xl" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{data.name}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6} display="flex" justifyContent="center">
            {data.type === MediaType.Image && <Image src={data.fileUrl} maxH="70vh" objectFit="contain" />}
            {data.type === MediaType.Video && <video src={data.fileUrl} controls style={{ maxHeight: '70vh', width: '100%' }} />}
            {data.type === MediaType.Audio && <audio src={data.fileUrl} controls style={{ width: '100%' }} />}
            {data.type === MediaType.Pdf && (
              <Box w="100%" h="70vh" border="1px solid rgba(0, 0, 0, 0.1)">
                <Viewer fileUrl={data.fileUrl!} />
              </Box>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

  {viewMode === 'list' ? (
    <Card w="full">
      <CardBody display='flex' alignItems='center' gap={4} py={2} px={4}>
        {/* Checkbox */}
        <Checkbox
          isChecked={selectedIds?.includes(data.id)}
          onChange={() => onToggleSelect?.(data.id)}
          colorScheme="blue"
          mr={1}
        />

        {/* Thumbnail/Icon */}
        <Box flexShrink={0}>
          <Box cursor='pointer' onClick={onOpen} w='100%' h='100%' display='flex' alignItems='center' justifyContent='center' position="relative" role="group">
            {data.type === MediaType.Video || data.type === MediaType.Image ? (
              <Image src={data.iconUrl} alt={data.name} boxSize='60px' objectFit='cover' borderRadius='md' />
            ) : (
              <Center boxSize='60px'>
                <FontAwesomeIcon icon={matchMediaIcon(data.type)} size='2x' />
              </Center>
            )}
            <Box position="absolute" inset={0} bg="blackAlpha.600" borderRadius="md" opacity={0} _groupHover={{ opacity: 1 }} transition="all 0.2s" display="flex" alignItems="center" justifyContent="center" color="white">
              <FontAwesomeIcon icon={faEye} />
            </Box>
          </Box>
        </Box>

        {/* References */}
        <Box flex='2' minW='0' textAlign={'center'}>
          <Text w='100%' textAlign='center' fontWeight="semibold">
            {data.references.length} references
          </Text>
          {resolvedPaths.length > 0 && (
            <Flex flexDir="column" gap={0.5} mt={0.5} align="center">
              {data.references.map((refId, idx) => {
                const pathStr = resolvedPaths[idx];
                if (!pathStr) return null;
                return (
                  <Link
                    key={refId}
                    fontSize="10px"
                    color="blue.500"
                    textDecoration="underline"
                    _hover={{ color: "blue.700" }}
                    onClick={() => navigate(`/${app?.id}/editor?select=${refId}`)}
                    isTruncated
                    maxW="140px"
                    display="block"
                  >
                    ({pathStr})
                  </Link>
                );
              })}
            </Flex>
          )}
        </Box>



        {/* Media Name & Tags */}
        <Box flex='3' minW='0' display="flex" flexDir="column" gap={1} alignSelf="center">
          <Text isTruncated fontWeight='medium' fontSize="sm">
            {data.name}
          </Text>
          {data.tags && data.tags.length > 0 && (
            <Flex gap={1} flexWrap="wrap">
              {data.tags.map(t => (
                <Badge key={t} size="xs" colorScheme="blue" borderRadius="full" px={2} py={0.1} fontSize="9px">
                  {t}
                </Badge>
              ))}
            </Flex>
          )}
        </Box>

        {/* File Size */}
        <Box flex='1' textAlign='center'>
          <Text fontWeight='bold'>{bytesToMB(data.size)}</Text>
        </Box>

        {/* Actions */}
        <Box flexShrink={0} minW='180px' position='relative' zIndex={2}>
          {newName !== undefined ? (
            <>
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                size='sm'
                mb={2}
                placeholder='Media Name'
              />
              <Center gap={2}>
                <IconButton
                  size='sm'
                  variant='outline'
                  colorScheme='green'
                  aria-label='Confirm Changes'
                  icon={<FontAwesomeIcon icon={faCheck} />}
                  onClick={() => {
                    const lastDotIndex = data.name.lastIndexOf('.')
                    const fileExtension = lastDotIndex === -1 ? '' : data.name.substring(lastDotIndex)
                    onEdit({ ...data, name: newName + fileExtension }, data)
                    setNewName(undefined)
                  }}
                />
                <IconButton
                  size='sm'
                  variant='outline'
                  colorScheme='red'
                  aria-label='Cancel Changes'
                  icon={<FontAwesomeIcon icon={faCancel} />}
                  onClick={() => setNewName(undefined)}
                />
              </Center>
            </>
          ) : isDeleting ? (
            <Center gap={2}>
              <IconButton
                size='sm'
                variant='outline'
                colorScheme='green'
                aria-label='Confirm Deletion'
                icon={<FontAwesomeIcon icon={faCheck} />}
                onClick={() => onDelete(data)}
              />
              <IconButton
                size='sm'
                variant='outline'
                colorScheme='red'
                aria-label='Cancel Deletion'
                icon={<FontAwesomeIcon icon={faCancel} />}
                onClick={() => setIsDeleting(false)}
              />
            </Center>
          ) : (
            <Center gap={2}>
              <IconButton variant='outline' colorScheme="blue" size='sm' aria-label="Edit Media Name" icon={<FontAwesomeIcon icon={faEdit} />} onClick={() => {
                const lastDotIndex = data.name.lastIndexOf('.')
                setNewName(lastDotIndex === -1 ? data.name : data.name.substring(0, lastDotIndex))
              }} />
              <IconButton
                variant='outline'
                colorScheme="teal"
                size='sm'
                aria-label="Replace File"
                icon={<FontAwesomeIcon icon={faUpload} />}
                onClick={() => fileInputRef.current?.click()}
              />
              <IconButton
                size='sm'
                variant='outline'
                colorScheme='red'
                aria-label='Delete Media'
                icon={<FontAwesomeIcon icon={faTrash} />}
                onClick={() => {
                  if (data.references?.length === 0) {
                    setIsDeleting(true)
                  } else {
                    setIsWarning(true)
                    toast({
                      title: 'Cannot delete media',
                      description: `${data.references.length} reference(s) are using this file.`,
                      status: 'warning',
                      duration: 4000,
                      isClosable: true,
                      position: 'top',
                    })
                  }
                }}
              />
            </Center>
          )}
        </Box>
      </CardBody>
    </Card>
  ) : (
    <Card h={330} display='flex' flexDir='column'>
      <CardHeader as='b' overflow='hidden' gap={2} display='flex' alignItems='center' justifyContent='space-between' p={3}>
        <Flex align="center" gap={2} overflow="hidden" minW={0} flex={1}>
          <Checkbox
            isChecked={selectedIds?.includes(data.id)}
            onChange={() => onToggleSelect?.(data.id)}
            colorScheme="blue"
          />
          <FontAwesomeIcon icon={matchMediaIcon(data.type)} size='lg' />
          <Text overflow='hidden' whiteSpace='nowrap' textOverflow='ellipsis' fontSize="sm">
            {isDeleting ? 'Confirm Deletion' : newName ? 'Change Name' : isWarning ? 'Cannot Delete' : data.name}
          </Text>
        </Flex>

        <Text fontWeight='bold' flexShrink={0} fontSize="xs">
          {bytesToMB(data.size)}
        </Text>
      </CardHeader>
      <CardBody flex={1} display='flex' flexDir='column' justifyContent='space-between' alignItems='center' minH={0} p={3}>
        <Box cursor='pointer' onClick={onOpen} w='100%' h='110px' display='flex' flexDir='column' alignItems='center' justifyContent='center' position="relative" role="group">
          {data.type === MediaType.Pdf || data.type === MediaType.Audio ? (
            <Center flex={1} w='full'>
              <FontAwesomeIcon icon={matchMediaIcon(data.type)} opacity={0.5} size='4x' />
            </Center>
          ) : (
            <Image src={data.iconUrl} fit='contain' w='full' h='100%' borderRadius="md" />
          )}
          <Box position="absolute" inset={0} bg="blackAlpha.600" borderRadius="md" opacity={0} _groupHover={{ opacity: 1 }} transition="all 0.2s" display="flex" alignItems="center" justifyContent="center" color="white">
            <FontAwesomeIcon icon={faEye} size="lg" />
          </Box>
        </Box>
        <Flex flexDir="column" align="center" gap={1} w="full" mt={2}>
          <Text w='100%' textAlign='center' fontWeight="semibold" fontSize="xs">
            {data.references.length} references
          </Text>
          {data.tags && data.tags.length > 0 && (
            <Flex gap={1} flexWrap="wrap" justify="center" maxH="20px" overflow="hidden">
              {data.tags.map(t => (
                <Badge key={t} size="xs" colorScheme="blue" borderRadius="full" px={2} py={0.1} fontSize="9px">
                  {t}
                </Badge>
              ))}
            </Flex>
          )}

        </Flex>
      </CardBody>
      <CardFooter display='flex' w='full' flexDir='column' gap={3} position='relative' zIndex={2}>
        {newName !== undefined ? (
          <>
            <Input value={newName} onChange={(e) => setNewName(e.target.value)} w='100%' placeholder='Media Name' />
            <Center w='100%' gap={3}>
              <IconButton
                variant='outline'
                colorScheme='green'
                flex={1}
                aria-label='Confirm Changes'
                icon={<FontAwesomeIcon icon={faCheck} />}
                onClick={() => {
                  const lastDotIndex = data.name.lastIndexOf('.')
                  const fileExtension = lastDotIndex === -1 ? '' : data.name.substring(lastDotIndex)
                  onEdit({ ...data, name: newName + fileExtension }, data)
                  setNewName(undefined)
                }}
              />
              <IconButton
                variant='outline'
                colorScheme='red'
                flex={1}
                aria-label='Cancel Changes'
                icon={<FontAwesomeIcon icon={faCancel} />}
                onClick={() => setNewName(undefined)}
              />
            </Center>
          </>
        ) : (
          <></>
        )}
        {isDeleting ? (
          <Center w='full' gap='3'>
            <IconButton
              variant='outline'
              colorScheme='green'
              flex={1}
              aria-label='Confirm Deletion'
              icon={<FontAwesomeIcon icon={faCheck} />}
              onClick={() => onDelete(data)}
            />
            <IconButton
              variant='outline'
              colorScheme='red'
              flex={1}
              aria-label='Cancel Deletion'
              icon={<FontAwesomeIcon icon={faCancel} />}
              onClick={() => setIsDeleting(false)}
            />
          </Center>
        ) : (
          <></>
        )}
        {newName === undefined && !isDeleting ? (
          <Center w='100%' gap={3}>
            <IconButton variant='outline' colorScheme="blue" flex={1} aria-label="Edit Media Name" icon={<FontAwesomeIcon icon={faEdit} />} onClick={() => {
              const lastDotIndex = data.name.lastIndexOf('.')
              setNewName(lastDotIndex === -1 ? data.name : data.name.substring(0, lastDotIndex))
            }} />
            <IconButton
              variant='outline'
              colorScheme="teal"
              flex={1}
              aria-label="Replace File"
              icon={<FontAwesomeIcon icon={faUpload} />}
              onClick={() => fileInputRef.current?.click()}
            />
            <IconButton
              variant='outline'
              colorScheme='red'
              flex={1}
              aria-label='Delete Media'
              icon={<FontAwesomeIcon icon={faTrash} />}
              onClick={() => {
                if (data.references?.length === 0) {
                  setIsDeleting(true)
                } else {
                  setIsWarning(true)
                  toast({
                    title: 'Cannot delete media',
                    description: `${data.references.length} reference(s) are using this file.`,
                    status: 'warning',
                    duration: 4000,
                    isClosable: true,
                    position: 'top',
                  })
                }
              }}
            />
          </Center>
        ) : (
          <></>
        )}
      </CardFooter>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept={getAcceptString(data.type)}
        style={{ display: 'none' }}
      />
    </Card>
  )}
  </>
  )
}

export default MediaCard
